import React, { useState, useRef, useEffect } from 'react';
import { judgeService } from '../../services/supabaseService';
import { storageServiceProxy, imageServiceProxy } from '../../services/proxyService';
import { useNotifications } from '../shared/useNotifications';
import { useAuth } from '../../context/AuthContext';
import { Upload, Save, X, User, Camera, FileText, Eye, Trash2 } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import './JudgeProfileEditor.css';

const JudgeProfileEditor = ({ judgeInfo, onUpdate, onCancel }) => {
  const { success: notifySuccess, error: notifyError, warning: notifyWarning } = useNotifications();
  const { updatePassword } = useAuth();
  const fileInputRef = useRef(null);
  const curriculumFileRef = useRef(null);
  
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Photo state (like Coach)
  const [judgePhotoUrl, setJudgePhotoUrl] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // CV state (like Coach)
  const [cvUrl, setCvUrl] = useState(judgeInfo?.curriculum_url || null);
  const [uploadingCv, setUploadingCv] = useState(false);
  
  const [formData, setFormData] = useState({
    name: judgeInfo?.name || '',
    email: judgeInfo?.email || '',
    phone: judgeInfo?.phone || '',
    address: judgeInfo?.address || '',
    bio: judgeInfo?.bio || '',
    experience: judgeInfo?.experience || '',
  });

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Load judge photo from storage on mount
  useEffect(() => {
    if (judgeInfo?.photo_url) {
      setJudgePhotoUrl(judgeInfo.photo_url);
    } else if (judgeInfo?.id) {
      // Try to find photo in images table
      (async () => {
        try {
          const { data: photoUrl } = await imageServiceProxy.getByEntity('judge', judgeInfo.id);
          if (photoUrl) setJudgePhotoUrl(photoUrl);
        } catch (e) { /* ignore */ }
      })();
    }
    if (judgeInfo?.curriculum_url) {
      setCvUrl(judgeInfo.curriculum_url);
    }
  }, [judgeInfo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Photo handling (like Coach - with compression)
  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 0.15, maxWidthOrHeight: 400, useWebWorker: true });
      setPhotoFile(compressed);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(compressed);
    } catch (err) { notifyError('Error al procesar la imagen'); }
  };

  const handleUploadPhoto = async () => {
    if (!photoFile) return;
    setUploadingPhoto(true);
    try {
      const fileExt = photoFile.name.split('.').pop();
      const fileName = `judges/${judgeInfo.id}-${Date.now()}.${fileExt}`;
      const result = await storageServiceProxy.upload('gymnastics-images', fileName, photoFile);
      if (!result.success) throw new Error(result.error);
      if (result.data?.publicUrl) {
        const photoUrl = result.data.publicUrl;
        setJudgePhotoUrl(photoUrl);
        setPhotoPreview(null);
        setPhotoFile(null);
        // Guardar en tabla images
        await imageServiceProxy.setImage('judge', judgeInfo.id, photoUrl);
        notifySuccess('Foto de perfil actualizada');
        if (onUpdate) onUpdate({ ...judgeInfo, photo_url: photoUrl });
      }
    } catch (err) { notifyError('Error al subir foto: ' + err.message); }
    finally { setUploadingPhoto(false); }
  };

  // CV handling (like Coach)
  const handleCvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { notifyWarning('Solo se permiten archivos PDF'); return; }
    if (file.size > 5 * 1024 * 1024) { notifyWarning('El archivo no puede superar 5MB'); return; }
    setUploadingCv(true);
    try {
      const fileName = `judges/cv-${judgeInfo.id}-${Date.now()}.pdf`;
      const result = await storageServiceProxy.upload('gymnastics-images', fileName, file);
      if (!result.success) throw new Error(result.error);
      const newUrl = result.data?.publicUrl;
      if (newUrl) {
        await judgeService.update(judgeInfo.id, { curriculum_url: newUrl });
        setCvUrl(newUrl);
        notifySuccess('Currículo subido exitosamente');
        if (onUpdate) onUpdate({ ...judgeInfo, curriculum_url: newUrl });
      }
    } catch (err) { notifyError('Error al subir currículo: ' + err.message); }
    finally { setUploadingCv(false); e.target.value = ''; }
  };

  const handleRemoveCv = async () => {
    try {
      await judgeService.update(judgeInfo.id, { curriculum_url: null });
      setCvUrl(null);
      notifySuccess('Currículo eliminado');
      if (onUpdate) onUpdate({ ...judgeInfo, curriculum_url: null });
    } catch (err) { notifyError('Error al eliminar currículo'); }
  };

  const handleJudgeChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    if (!newPassword) { setPasswordError('La nueva contraseña es obligatoria'); return; }
    if (newPassword.length < 6) { setPasswordError('La contraseña debe tener al menos 6 caracteres'); return; }
    if (newPassword !== confirmNewPassword) { setPasswordError('Las contraseñas no coinciden'); return; }
    setChangingPassword(true);
    try {
      const { error } = await updatePassword(newPassword);
      if (error) throw new Error(error);
      setPasswordSuccess('Contraseña actualizada correctamente');
      setNewPassword('');
      setConfirmNewPassword('');
      setShowPasswordForm(false);
      notifySuccess('Contraseña actualizada');
    } catch (err) { setPasswordError(err.message || 'Error al cambiar contraseña'); }
    finally { setChangingPassword(false); }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { notifyWarning('El nombre es obligatorio'); return; }
    setIsSaving(true);
    try {
      const updateData = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        bio: formData.bio.trim(),
        experience: formData.experience.trim(),
      };

      const { error } = await judgeService.update(judgeInfo.id, updateData);
      
      if (error) {
        notifyError('Error al actualizar el perfil');
        return;
      }

      notifySuccess('Perfil actualizado correctamente');
      setIsEditing(false);
      
      if (onUpdate) {
        onUpdate({ ...judgeInfo, ...updateData });
      }
    } catch (err) {
      notifyError('Error al guardar el perfil');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      name: judgeInfo?.name || '',
      email: judgeInfo?.email || '',
      phone: judgeInfo?.phone || '',
      address: judgeInfo?.address || '',
      bio: judgeInfo?.bio || '',
      experience: judgeInfo?.experience || '',
    });
    setPhotoPreview(null);
    setPhotoFile(null);
    if (onCancel) onCancel();
  };

  const getInitials = (name) => (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="jpe-container">
      <div className="jpe-header">
        <h2><User size={20} /> Mi Perfil</h2>
        {!isEditing ? (
          <button className="btn btn-judge-primary" onClick={() => setIsEditing(true)}>
            Editar perfil
          </button>
        ) : (
          <div className="jpe-action-buttons">
            <button 
              className="btn btn-judge-primary" 
              onClick={handleSave}
              disabled={isSaving}
            >
              <Save size={16} /> {isSaving ? 'Guardando...' : 'Guardar'}
            </button>
            <button 
              className="btn btn-judge-secondary" 
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X size={16} /> Cancelar
            </button>
          </div>
        )}
      </div>

      <div className="jpe-content">
        {/* Foto de Perfil - Style like Coach */}
        <div className="jpe-photo-upload-section">
          <div className="jpe-photo-wrapper">
            <div className="jpe-photo-circle">
              {(photoPreview || judgePhotoUrl) ? (
                <img src={photoPreview || judgePhotoUrl} alt="Foto" />
              ) : (
                <span className="jpe-photo-initials">{getInitials(formData.name)}</span>
              )}
            </div>
            <label className="jpe-photo-camera-btn" title="Cambiar foto">
              <Camera size={16} />
              <input type="file" accept="image/*" onChange={handlePhotoChange} hidden />
            </label>
          </div>
          <div className="jpe-photo-info">
            <span className="jpe-photo-name">{formData.name || 'Tu nombre'}</span>
            <span className="jpe-photo-hint">JPG, PNG — máx. 2MB</span>
            {photoPreview && (
              <div className="jpe-photo-btns">
                <button className="btn btn-judge-primary" onClick={handleUploadPhoto} disabled={uploadingPhoto}>
                  {uploadingPhoto ? 'Subiendo...' : 'Guardar Foto'}
                </button>
                <button className="btn btn-judge-secondary" onClick={() => { setPhotoPreview(null); setPhotoFile(null); }}>
                  Cancelar
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Información Básica */}
        <div className="jpe-form-section">
          <h3>Información Básica</h3>
          <div className="jpe-form-grid">
            <div className="jpe-form-group">
              <label>Nombre Completo</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={!isEditing ? 'jpe-input-disabled' : ''}
              />
            </div>
            <div className="jpe-form-group">
              <label>Correo Electrónico</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                disabled
                className="jpe-input-disabled"
              />
            </div>
            <div className="jpe-form-group">
              <label>Teléfono</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={!isEditing ? 'jpe-input-disabled' : ''}
                placeholder="Ej: +1 234 567 8900"
              />
            </div>
            <div className="jpe-form-group">
              <label>Dirección</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                disabled={!isEditing}
                className={!isEditing ? 'jpe-input-disabled' : ''}
                placeholder="Ciudad, País"
              />
            </div>
          </div>
        </div>

        {/* Información Profesional */}
        <div className="jpe-form-section">
          <h3>Información Profesional</h3>
          
          <div className="jpe-form-group jpe-full">
            <label>Sobre mí</label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              disabled={!isEditing}
              className={!isEditing ? 'jpe-input-disabled' : ''}
              placeholder="Cuéntanos sobre ti..."
              rows="3"
            />
          </div>

          <div className="jpe-form-group jpe-full">
            <label>Experiencia</label>
            <textarea
              name="experience"
              value={formData.experience}
              onChange={handleInputChange}
              disabled={!isEditing}
              className={!isEditing ? 'jpe-input-disabled' : ''}
              placeholder="Describe tu experiencia como juez..."
              rows="3"
            />
          </div>

          {/* Currículum - Style like Coach */}
          <div className="jpe-form-group jpe-full" style={{ marginTop: 8 }}>
            <label><FileText size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Currículo (PDF)</label>
            <div className="jpe-cv-upload-area">
              {cvUrl ? (
                <div className="jpe-cv-current">
                  <div className="jpe-cv-icon"><FileText size={24} /></div>
                  <div className="jpe-cv-info">
                    <span className="jpe-cv-label">Currículo cargado</span>
                    <div className="jpe-cv-actions">
                      <a href={cvUrl} target="_blank" rel="noopener noreferrer" className="jpe-cv-btn jpe-cv-btn-view">
                        <Eye size={14} /> Ver PDF
                      </a>
                      <button className="jpe-cv-btn jpe-cv-btn-delete" onClick={handleRemoveCv}>
                        <Trash2 size={14} /> Eliminar
                      </button>
                      <label className="jpe-cv-btn jpe-cv-btn-replace">
                        <Upload size={14} /> Reemplazar
                        <input type="file" accept="application/pdf" onChange={handleCvUpload} hidden />
                      </label>
                    </div>
                  </div>
                </div>
              ) : (
                <label className="jpe-cv-dropzone">
                  <Upload size={28} />
                  <span>{uploadingCv ? 'Subiendo...' : 'Subir Currículo en PDF'}</span>
                  <span className="jpe-cv-hint">Máximo 5MB</span>
                  <input type="file" accept="application/pdf" onChange={handleCvUpload} hidden disabled={uploadingCv} />
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Save button at the bottom (like Coach) */}
        {isEditing && (
          <button className="btn btn-judge-primary jpe-save-bottom" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Guardando...' : 'Guardar Perfil'}
          </button>
        )}

        {/* Change Password Section */}
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #e4e4e7' }}>
          {!showPasswordForm ? (
            <button className="btn btn-judge-secondary" onClick={() => setShowPasswordForm(true)}>
              Cambiar Contraseña
            </button>
          ) : (
            <div style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '0.95rem' }}>Nueva Contraseña</h4>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}>Nueva Contraseña</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Mínimo 6 caracteres" style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d4d4d8' }} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}>Confirmar Contraseña</label>
                <input type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} placeholder="Repite la contraseña" style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d4d4d8' }} />
              </div>
              {passwordError && <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: '8px 0' }}>{passwordError}</p>}
              {passwordSuccess && <p style={{ color: '#059669', fontSize: '0.85rem', margin: '8px 0' }}>{passwordSuccess}</p>}
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button className="btn btn-judge-primary" onClick={handleJudgeChangePassword} disabled={changingPassword}>
                  {changingPassword ? 'Cambiando...' : 'Confirmar'}
                </button>
                <button className="btn btn-judge-secondary" onClick={() => { setShowPasswordForm(false); setNewPassword(''); setConfirmNewPassword(''); setPasswordError(''); }}>
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JudgeProfileEditor;
